// http://www.critterai.org/projects/nmgen_study/voxelization.html

import { vec2, vec3 } from "gl-matrix";
import { Face, TraversabilityType } from "@spi/Common/Geometry";
import { intersectsFace } from "../Math/math";
import { BoundingBox, BoundingBoxContainment } from '@spi/Common/Util';
import { ConditionalGate } from "./ConditionalGate";
import { HeightSpan, SpanType } from "./HeightSpan";
import { NavigationMeshSettings } from "./NavigationMesh";
import { Voxel, VoxelFilling } from "./Voxel";

export class HeightField {

    public voxelMap: Map<string, VoxelHeightMap>;
    public spanMap: Map<string, HeightSpan[]>;
    public openSpanMap: Map<string, HeightSpan[]>;

    constructor(public settings: NavigationMeshSettings, public bounds: BoundingBox) {
        this.voxelMap = new Map<string, VoxelHeightMap>();
        this.openSpanMap = new Map<string, HeightSpan[]>();
        this.spanMap = new Map<string, HeightSpan[]>();
    }

    public get voxelSize() {
        return this.settings.voxelSize;
    }

    public voxelizeGeometry(...faces: Face[]): void {
        for (let face of faces) {

            let faceBounds = face.getBoundingBox();

            if (faceBounds.isContained(this.bounds) == BoundingBoxContainment.None) {
                continue;
            }

            let voxels = this.getFaceVoxels(face);
            for (let voxel of voxels) {
                if (intersectsFace(voxel, face)) {
                    voxel.setFilling(VoxelFilling.Full);
                    voxel.traversability = face.traversability;
                    voxel.stepHeightOverride = face.stepHeightOverride;
                }
            }
        }
    }

    public voxelizeConditionalGates(...gates: ConditionalGate[]): void {
        for (let gate of gates) {
            for (let face of gate.faces) {

                let faceBounds = face.getBoundingBox();

                if (faceBounds.isContained(this.bounds) == BoundingBoxContainment.None) {
                    continue;
                }

                let voxels = this.getFaceVoxels(face);
                for (let voxel of voxels) {
                    if (intersectsFace(voxel, face)) {
                        voxel.setFilling(VoxelFilling.Conditional, gate.gateReference);
                        voxel.traversability = face.traversability;
                        voxel.stepHeightOverride = face.stepHeightOverride;
                    }
                }
            }
        }
    }

    private determineApproximateHeightAverage(face: Face): number {
        let sum = 0;
        face.iterVertices(v => sum += v.position[2]);

        return sum / face.vertices.length;
    }

    private getFaceVoxels(face: Face): Voxel[] {

        let boundingBox = face.getBoundingBox();
        let approximateHeight = this.determineApproximateHeightAverage(face);

        let voxelizer = new BruteForceVoxelizer(this);
        //let voxelizer = new PartitionedVoxelizer(this, face);

        let voxels = voxelizer.determineVoxels(boundingBox);

        voxels.forEach(voxel => voxel.addApproximateHeight(approximateHeight));

        return voxels;
    }

    public getVoxel(location: vec3 | number[], autoCreate: boolean): Voxel {
        let x = location[0];
        let y = location[1];
        let z = location[2];

        let voxelLocation = x + ',' + y;
        let zLevel = '' + z;

        let min = [x * this.voxelSize, y * this.voxelSize, z * this.voxelSize];
        let max = [(x + 1) * this.voxelSize, (y + 1) * this.voxelSize, (z + 1) * this.voxelSize];

        if (new BoundingBox(min, max).isContained(this.bounds) != BoundingBoxContainment.Full) {
            return null;
        }

        let voxelHeightMap: VoxelHeightMap;

        if (this.voxelMap.has(voxelLocation)) {
            voxelHeightMap = this.voxelMap.get(voxelLocation);
        } else {

            if (!autoCreate) {
                return null;
            }

            voxelHeightMap = {
                voxels: [],
                map: {}
            };

            this.voxelMap.set(voxelLocation, voxelHeightMap);
        }

        if (voxelHeightMap.map[zLevel]) {
            return voxelHeightMap.map[zLevel];
        } else {

            if (!autoCreate) {
                return null;
            }

            let voxel = new Voxel(
                [x, y, z],
                min,
                max
            );

            voxelHeightMap.voxels.push(voxel);
            voxelHeightMap.map[zLevel] = voxel;

            return voxel;
        }
    }

    private generateSolidSpans(): void {

        let voxelSort = (a: Voxel, b: Voxel) => {
            return a.voxelLocation[2] - b.voxelLocation[2];
        };

        this.voxelMap.forEach((voxelHeightMap, voxelPosition) => {
            let voxels = voxelHeightMap.voxels;
            voxels.sort(voxelSort);

            let spanList: HeightSpan[] = [];
            let currentSpan: HeightSpan = null;

            for (let voxel of this.expandVoxelList(voxels)) {

                if (!voxel) {
                    currentSpan = null;
                    continue;
                }

                if (voxel.filling != VoxelFilling.Empty) {

                    if (voxel.filling == VoxelFilling.Conditional) {
                        if (currentSpan) {
                            if (currentSpan.groupReference != voxel.conditional) {
                                currentSpan = null;
                            }
                        }
                    }

                    let approximateHeight = voxel.getApproximateHeight();

                    if (!currentSpan) {

                        currentSpan = new HeightSpan(
                            vec2.fromValues(voxel.voxelLocation[0], voxel.voxelLocation[1]),
                            approximateHeight,
                            approximateHeight
                        );

                        if (voxel.filling == VoxelFilling.Conditional) {
                            currentSpan.groupReference = voxel.conditional;
                            currentSpan.spanType = SpanType.Conditional;
                        }

                        if (spanList.length > 0) {
                            spanList[spanList.length - 1].nextSpan = currentSpan;
                        }

                        spanList.push(currentSpan);
                    }

                    currentSpan.stepHeightOverride = voxel.stepHeightOverride;
                    currentSpan.traversability = voxel.traversability;
                    currentSpan.top = approximateHeight;

                } else {
                    currentSpan = null;
                }
            }
            if (spanList.length > 0) {
                this.spanMap.set(voxelPosition, spanList);
            }

        });
    }

    private expandVoxelList(sortedVoxels: Voxel[]): Voxel[] {
        if (sortedVoxels.length <= 1) {
            // nothing to expand with an empty or single item list
            return sortedVoxels;
        }

        let first = sortedVoxels[0];
        let last = sortedVoxels[sortedVoxels.length - 1];
        let result: Voxel[] = [];
        for (let z = first.voxelLocation[2]; z <= last.voxelLocation[2]; z++) {
            result.push(this.getVoxel([first.voxelLocation[0], first.voxelLocation[1], z], false));
        }

        return result;
    }

    private generateOpenSpans(): void {

        this.spanMap.forEach((spanList, voxelPosition) => {
            let openSpanList: HeightSpan[] = [];

            for (let span of spanList) {

                if (span.spanType == SpanType.Conditional) {
                    let openSpan = new HeightSpan(
                        span.spanLocation,
                        span.bottom,
                        span.top
                    );
                    openSpan.groupReference = span.groupReference;
                    openSpan.traversable = true;
                    openSpan.spanType = SpanType.Open;
                    openSpan.stepHeightOverride = span.stepHeightOverride;
                    openSpanList.push(openSpan);
                }

                if (span.spanType == SpanType.Solid || span.spanType == SpanType.Conditional) {

                    if (span.traversable) {
                        let openSpan = new HeightSpan(
                            span.spanLocation,
                            span.top,
                            span.nextSpan ? span.nextSpan.bottom : 99999
                        );

                        openSpan.traversable = true;
                        openSpan.spanType = SpanType.Open;
                        openSpan.stepHeightOverride = span.stepHeightOverride;
                        openSpanList.push(openSpan);
                    }
                }
            }

            if (openSpanList.length > 0) {
                this.openSpanMap.set(voxelPosition, openSpanList);
            }
        });
    }

    private determineNeighbours() {
        this.openSpanMap.forEach(spanList => spanList.forEach(span => span.determineNeighbours(this.openSpanMap, this.settings.maxNeighbourStepHeight)));
    }

    private determineTraversability(): void {

        this.spanMap.forEach(spanList => {
            for (let span of spanList) {

                switch (span.traversability) {
                    case TraversabilityType.Open:
                        span.traversable = true;
                        console.warn('Traversability Open is a bugged feature, needs to be fixed. Do not use.');
                        break;
                    case TraversabilityType.Blocked:
                        span.traversable = false;
                        break;
                    case TraversabilityType.Natural:
                        span.traversable = true; // by default assume true
                        if (span.nextSpan) {
                            if (span.nextSpan.bottom - span.top < this.settings.minTraversableHeight) {
                                span.traversable = false;
                            }
                        }
                        break;
                }
            }
        });
    }

    public generate() {
        this.generateSolidSpans();
        this.voxelMap = null;

        this.determineTraversability();
        this.generateOpenSpans();
        this.spanMap = null;

        this.determineNeighbours();
    }
}

type VoxelHeightMap = {
    voxels: Voxel[],
    map: { [key: string]: Voxel }
}

interface IVoxelizer {
    determineVoxels(boundingBox: BoundingBox): Voxel[];
}

class BruteForceVoxelizer implements IVoxelizer {

    constructor(private heightField: HeightField) {

    }

    determineVoxels(boundingBox: BoundingBox): Voxel[] {
        let voxels: Voxel[] = [];
        let boxMin = boundingBox.min;
        let boxMax = boundingBox.max;

        let min = [
            Math.floor(boxMin[0] / this.heightField.voxelSize),
            Math.floor(boxMin[1] / this.heightField.voxelSize),
            Math.floor(boxMin[2] / this.heightField.voxelSize)
        ];

        let max = [
            Math.ceil(boxMax[0] / this.heightField.voxelSize),
            Math.ceil(boxMax[1] / this.heightField.voxelSize),
            Math.ceil(boxMax[2] / this.heightField.voxelSize)
        ];

        for (let x = min[0]; x <= max[0]; x++) {
            for (let y = min[1]; y <= max[1]; y++) {
                for (let z = min[2]; z <= max[2]; z++) {
                    let voxel = this.heightField.getVoxel([x, y, z], true);

                    if (voxel) {
                        voxels.push(voxel);
                    }
                }
            }
        }

        return voxels;
    }
}

class PartitionedVoxelizer implements IVoxelizer {

    constructor(private heightField: HeightField, private face: Face) {

    }

    determineVoxels(boundingBox: BoundingBox): Voxel[] {
        let voxels = new Set<Voxel>();

        for (let bounds of this.divide(boundingBox.min, boundingBox.max)) {

            let min = [
                Math.floor(bounds.min[0] / this.heightField.voxelSize),
                Math.floor(bounds.min[1] / this.heightField.voxelSize),
                Math.floor(bounds.min[2] / this.heightField.voxelSize)
            ];

            let max = [
                Math.ceil(bounds.max[0] / this.heightField.voxelSize),
                Math.ceil(bounds.max[1] / this.heightField.voxelSize),
                Math.ceil(bounds.max[2] / this.heightField.voxelSize)
            ];

            for (let x = min[0]; x <= max[0]; x++) {
                for (let y = min[1]; y <= max[1]; y++) {
                    for (let z = min[2]; z <= max[2]; z++) {
                        let voxel = this.heightField.getVoxel([x, y, z], true);

                        if (voxel) {
                            voxels.add(voxel);
                        }
                    }
                }
            }
        }

        return [...voxels];
    }

    private divide(min: vec3 | number[], max: vec3 | number[]): { min: number[], max: number[] }[] {
        let sub = vec3.sub(vec3.create(), max, min);
        let div = vec3.div(vec3.create(), sub, [2, 2, 2]);

        let modifiers = [
            [0, 0, 0],
            [1, 0, 0],
            [1, 1, 0],
            [1, 1, 1],
            [0, 0, 1],
            [0, 1, 1],
            [1, 0, 1],
            [0, 1, 0]
        ];

        let result = [];

        for (let mod of modifiers) {
            let sMin = [
                min[0] + (div[0] * mod[0]),
                min[1] + (div[1] * mod[1]),
                min[2] + (div[2] * mod[2])
            ];
            let sMax = [
                min[0] + div[0] + (div[0] * mod[0]),
                min[1] + div[1] + (div[1] * mod[1]),
                min[2] + div[2] + (div[2] * mod[2])
            ];

            let bounds = { min: sMin, max: sMax };

            if (intersectsFace(bounds, this.face)) {
                result.push(bounds);
            }
        }

        return result;

    }

}